import { PencilIcon } from '@/components/IconButton';
import { PopupMenu } from '@/components/PopupMenu';
import { ANALYTICS_EVENT_OBJECT } from '@/constants/analytics';
import { ANNOTATION_SHAPE_TYPE } from '@/constants/annotation';
import { EXPLORE_ROOT } from '@/constants/paths';
import { canPolicyActionEdit } from '@/constants/policy';
import { queryClient } from '@/graphql/client';
import {
  AnnotationSelectField,
  AnnotationTemplate,
  CreateAnnotationInput,
  CreateAnnotationMutationVariables,
  DeleteAnnotationMutationVariables,
  UpdateAnnotationMutationVariables,
} from '@/graphql/codegen/graphql';
import { CREATE_ANNOTATION, DELETE_ANNOTATION, UPDATE_ANNOTATION } from '@/graphql/mutations';
import { request } from '@/graphql/request';
import { useFetchAnnotationHistory } from '@/hooks/useFetchAnnotationHistory';
import { useFetchAnnotations, useRefetchAnnotations } from '@/hooks/useFetchAnnotations';
import { useFetchCountTemplateAnnotations } from '@/hooks/useFetchCountTemplateAnnotations';
import { useFetchProcesses } from '@/hooks/useFetchProcesses';
import { useFetchSceneEntityPermissions } from '@/hooks/useFetchSceneEntityPermissions';
import { useTabsComponents } from '@/next/utils/useTabsComponents';
import {
  cancelEditAnnotation,
  getAnnotation3dMutationInput,
  saveEditAnnotation,
  saveEditAnnotationWithoutClosingPopupMenu,
  setAnnotationMeasurements,
  setAnnotationOriginal,
  setAnnotationOriginalCache,
  setAnnotationOriginalSectionsCache,
  setAnnotationOriginalVolumetricsCache,
  setCurrentAnnotation,
  setLastCreatedAnnotationId,
  startUpdateAnnotation,
  useExplore,
} from '@/stores/explore';
import {
  Layer,
  loadLayerModel,
  setTargetAnnotation2D,
  setTargetAnnotation3D,
  startDraw2D,
  startDraw3D,
  startPanoramaWalkthrough,
  startPhotoOverlay,
  stopDraw3D,
  stopPanoramaView,
  stopPhotoOverlay,
  useViewer,
} from '@/stores/viewer';
import { cn } from '@/utils/classname';
import { getShareLinkToken } from '@/utils/shareLink';
import { createAnnotation as transformCreateAnnotation } from '@/utils/transformers';
import { PolygonSketch2 } from '@/utils/Viewer2D/Editor/Sketch2';
import { Button, Tooltip, TooltipContent, TooltipTrigger, chip, chipIcon, toast } from '@skand/ui';
import {
  Cartographic,
  Model3D,
  ModelNode,
  SceneNode,
  SectioningProgressCallback,
  Sketch,
  VolumetricBaseMode,
  VolumetricProgressCallback,
  Volumetrics,
} from '@skand/viewer-component-v2';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Color, Matrix4, Quaternion, Vector3 } from 'three';
import { AnnotationDetails } from './AnnotationDetails';
import { getMeasurementDifferences, isDraftUntouched } from './AnnotationDetails/utils';
import { AnnotationMeasurements } from './AnnotationMeasurements';
import { VolumetricBaseOption } from './AnnotationMeasurements/AnnotationMeasurements';
import { AnnotationPopupIconButton } from './AnnotationPopupIconButton';
import { CancellationModal } from './CancellationModal';
import { DeletionModal } from './DeletionModal';
import { SaveCalculationModal } from './SaveCalculationModal';
import { ShapeTypeInfoModal } from './ShapeTypeInfoModal';
import { getAnnotationColor } from './utils';

export interface CreatedAnnotationInfo {
  annotationId: string;
  annotationGroupId: string;
}

export const AnnotationPopupMenu = () => {
  const api3D = useViewer(state => state.api3D);
  const api2D = useViewer(state => state.api2D);
  const loadingMutex = useViewer(state => state.loadingMutex);
  const enabledPanoramaWalkthrough = useViewer(state => state.enabledPanoramaWalkthrough);
  const enabledDraw3D = useViewer(state => state.enabledDraw3D);
  const annotationGroups = useViewer(state => state.annotationGroups);
  const targetProcess = useViewer(state => state.targetProcess);
  const photo2DGroups = useViewer(state => state.photo2DGroups);
  const layers = useViewer(state => state.layers);
  const targetAnnotation3D = useViewer(state => state.targetAnnotation3D);

  const showLocationAlertModal = useExplore(state => state.showLocationAlertModal);
  const annotationOriginalCache = useExplore(state => state.annotationOriginalCache);
  const annotationOriginalSectionsCache = useExplore(
    state => state.annotationOriginalSectionsCache,
  );
  const annotationOriginalVolumetricsCache = useExplore(
    state => state.annotationOriginalVolumetricsCache,
  );

  const annotationDraft = useExplore(state => state.annotationDraft);
  const projectId = useExplore(state => state.projectId);
  const annotationOriginal = useExplore(state => state.annotationOriginal);
  const isEditingAnnotationSketch = useExplore(state => state.isEditingAnnotationSketch);
  const annotationLayerCache = useExplore(state => state.annotationLayerCache);
  const annotationBaseSurfaceCache = useExplore(state => state.annotationBaseSurfaceCache);

  const [enabledDeletionModal, setEnabledDeletionModal] = useState(false);
  const [enabledCancellationModal, setEnabledCancellationModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [createdAnnotationInfo, setCreatedAnnotationInfo] = useState<CreatedAnnotationInfo | null>(
    null,
  );
  const [hasJustSwitchedToDetailsTab, setHasJustSwitchedToDetailsTab] = useState(false);
  const [currentTab, originalSetCurrentTab] = useState('details');
  const [enabledShapeTypeInfoModal, setEnableShapeTypeInfoModal] = useState(false);
  const [sections, setSections] = useState<Cartographic[][] | null>(null);
  const [volumetrics, setVolumetrics] = useState<Volumetrics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [baseSurface, setBaseSurface] = useState<VolumetricBaseOption>(
    annotationBaseSurfaceCache ?? 'triangulated',
  );
  const [baseHeight, setBaseHeight] = useState<number>(0);
  const [selectedLayer, setSelectedLayer] = useState<Layer | null>(annotationLayerCache ?? null);
  const [enabledSaveCalculationModal, setEnabledSaveCalculationModal] = useState(false);

  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStage, setLoadingStage] = useState('');

  const isPresetNameEnabled = annotationDraft?.mode === 'create';

  const setCurrentTab = useCallback(
    (nextTab: string) => {
      originalSetCurrentTab(prev => {
        if (prev !== nextTab && nextTab === 'details') {
          setHasJustSwitchedToDetailsTab(true);
        }
        return nextTab;
      });
    },
    [originalSetCurrentTab],
  );

  const { refetch: refetchAnnotations } = useRefetchAnnotations();
  const { getSceneEntityPermission } = useFetchSceneEntityPermissions();
  const shareLinkToken = getShareLinkToken();

  // Mutation for creating an annotation
  const createAnnotation = useMutation({
    mutationFn: (variables: CreateAnnotationMutationVariables) =>
      request(CREATE_ANNOTATION, variables),
    onSuccess: data => {
      if (annotationDraft?.groupId) {
        queryClient.invalidateQueries(useFetchAnnotations.getQueryKey(annotationDraft.groupId));
        refetchAnnotations([annotationDraft.groupId]);
      }
      if (annotationDraft?.templateId) {
        queryClient.invalidateQueries(
          useFetchCountTemplateAnnotations.getQueryKey(annotationDraft.templateId),
        );
      }
      if (data.createAnnotation?.annotationId && annotationDraft?.groupId) {
        setCreatedAnnotationInfo({
          annotationId: data.createAnnotation?.annotationId,
          annotationGroupId: annotationDraft.groupId,
        });
      }
      queryClient.invalidateQueries(useFetchProcesses.getQueryKey(projectId));
      saveEditAnnotation(data.createAnnotation?.annotationId ?? undefined, false);
      setLastCreatedAnnotationId(data.createAnnotation?.annotationId);
      toast({ message: 'Successfully created.', lifespan: 5000, clickToDismiss: true });
    },
    onError: error => {
      toast({
        type: 'warn',
        message: `${error}`,
        lifespan: 10000,
        clickToDismiss: true,
      });
    },
  });

  // Mutation for updating an annotation
  const updateAnnotation = useMutation({
    mutationFn: (variables: UpdateAnnotationMutationVariables) =>
      request(UPDATE_ANNOTATION, variables),
    onSuccess: data => {
      if (annotationDraft?.groupId) {
        queryClient.invalidateQueries(useFetchAnnotations.getQueryKey(annotationDraft.groupId));
        const refetchGroupIds = [annotationDraft.groupId];
        if (
          annotationOriginal?.groupId !== annotationDraft.groupId &&
          annotationOriginal?.groupId
        ) {
          refetchGroupIds.push(annotationOriginal.groupId);
        }
        refetchAnnotations(refetchGroupIds);
      }
      queryClient.invalidateQueries(
        useFetchAnnotationHistory.getQueryKey(annotationDraft?.annotationId ?? null),
      );
      useViewer.setState(state => {
        for (const group of state.annotationGroups) {
          group.loadState = 'pending';
        }
        return { annotationGroups: [...state.annotationGroups] };
      });

      if (annotationDraft) annotationDraft.sketch3D?.destroy();
      setTargetAnnotation3D(null);
      setTargetAnnotation2D(null);
      saveEditAnnotationWithoutClosingPopupMenu(data.updateAnnotation?.annotationId ?? undefined);
      toast({ message: 'Successfully updated.', lifespan: 5000, clickToDismiss: true });
    },
    onError: error => {
      toast({
        type: 'warn',
        message: (error as Error).message,
        lifespan: 10000,
        clickToDismiss: true,
      });
    },
  });

  const deleteAnnotation = useMutation({
    mutationFn: (variables: DeleteAnnotationMutationVariables) =>
      request(DELETE_ANNOTATION, variables),
    onSuccess: () => {
      destroy2DSketches();
      if (annotationDraft?.groupId) {
        queryClient.invalidateQueries(useFetchAnnotations.getQueryKey(annotationDraft.groupId));
        refetchAnnotations([annotationDraft.groupId]);
      }
      useViewer.setState(state => {
        for (const group of state.annotationGroups) {
          group.loadState = 'pending';
        }
        return { annotationGroups: [...state.annotationGroups] };
      });
    },
  });

  const annotation = useMemo(() => {
    for (const group of annotationGroups) {
      for (const annotation of group.annotations) {
        if (annotation.id === annotationDraft?.annotationId) {
          return annotation;
        }
      }
    }
    return undefined;
  }, [annotationDraft, annotationGroups]);

  const groupPermission = getSceneEntityPermission(annotation?.group.sceneEntityId ?? null);

  const canEdit = useMemo(() => {
    if (getShareLinkToken() !== null) return false;
    if (annotationDraft?.mode === 'create') return true;
    if (annotationDraft?.mode === 'preview') return false;
    return canPolicyActionEdit(groupPermission);
  }, [annotationDraft, groupPermission]);

  const metadata = useMemo(() => {
    // If annotationOriginalVolumetricsCache or annotationOriginalSectionsCache is available, fetch metadata from annotationOriginalCache instead of annotationDraft.
    if (
      (annotationOriginalVolumetricsCache || annotationOriginalSectionsCache) &&
      annotationOriginalCache
    ) {
      const group = annotationGroups.find(group => group.id === annotationOriginalCache?.groupId);
      const transform = new Matrix4().compose(
        group?.sceneNode?.getPosition() ?? new Vector3(),
        group?.sceneNode?.getRotation() ?? new Quaternion(),
        group?.sceneNode?.getScale() ?? new Vector3(1, 1, 1),
      );
      const points = annotationOriginalCache?.annotation3d?.positions?.map(point =>
        new Vector3(point?.x ?? 0, point?.y ?? 0, point?.z ?? 0).applyMatrix4(transform),
      );
      return {
        points,
        isClosed: annotationOriginalCache.annotation3d?.shapeType === ANNOTATION_SHAPE_TYPE.POLYGON,
      };
    } else if (
      annotationDraft?.annotation3d?.positions &&
      annotationDraft?.annotation3d?.shapeType
    ) {
      const group = annotationGroups.find(group => group.id === annotationDraft?.groupId);
      const transform = new Matrix4().compose(
        group?.sceneNode?.getPosition() ?? new Vector3(),
        group?.sceneNode?.getRotation() ?? new Quaternion(),
        group?.sceneNode?.getScale() ?? new Vector3(1, 1, 1),
      );
      const points = annotationDraft?.annotation3d?.positions?.map(point =>
        new Vector3(point?.x ?? 0, point?.y ?? 0, point?.z ?? 0).applyMatrix4(transform),
      );
      return {
        points,
        isClosed: annotationDraft.annotation3d?.shapeType === ANNOTATION_SHAPE_TYPE.POLYGON,
      };
    }
  }, [
    annotationDraft?.annotation3d?.positions,
    annotationDraft?.annotation3d?.shapeType,
    annotationDraft?.groupId,
    annotationGroups,
    annotationOriginalCache,
    annotationOriginalSectionsCache,
    annotationOriginalVolumetricsCache,
  ]);

  const shapeType = useMemo(() => {
    if (!metadata || !metadata?.points) return;
    if (metadata?.points.length === 1) return 'Point';
    else if (metadata.isClosed) return 'Polygon';
    else return 'Polyline';
  }, [metadata]);

  // Annotation group options
  const groupOptions = useMemo(() => {
    const options = [];
    for (const group of annotationGroups) {
      const access = getSceneEntityPermission(group.sceneEntityId);
      if (annotationDraft?.mode === 'update' || canPolicyActionEdit(access)) {
        options.push({
          key: group.id,
          name: group.name,
        });
      }
    }
    return options;
  }, [annotationDraft?.mode, annotationGroups, getSceneEntityPermission]);

  const canSave = useMemo(() => {
    return canEdit || annotationDraft?.mode === 'preview';
  }, [annotationDraft, canEdit]);

  const isDirty = useMemo(() => {
    if (annotationDraft?.mode === 'create') return true;
    if (!annotationDraft || !annotationOriginal) return true;
    return !isDraftUntouched(annotationDraft, annotationOriginal);
  }, [annotationDraft, annotationOriginal]);

  const handleFlyTo = async () => {
    await handleViewAssociatedImage();
    if (api3D && annotation?.sketch3D) {
      api3D.navigation.lookAt(annotation.sketch3D);
    } else if (api2D && annotation?.sketch2D) {
      api2D.editor.lookAt(annotation.sketch2D);
    }
  };

  const handleViewAssociatedImage = async () => {
    if (annotation?.photo && !loadingMutex) {
      stopPhotoOverlay();
      stopPanoramaView();

      if (annotation.photo.type === 'photo2D') {
        await startPhotoOverlay(annotation.photo, !annotation.sketch3D, false, false);
      } else {
        await startPanoramaWalkthrough(annotation.photo);
      }
    }
  };

  const handleShareLink = async () => {
    if (annotation && projectId) {
      const base = window.location.origin + EXPLORE_ROOT;
      const params = new URLSearchParams([
        ['project', projectId],
        ['annotation', annotation.id],
      ]);
      if (shareLinkToken) {
        params.append('shareLinkToken', shareLinkToken);
      }
      const url = `${base}?${params.toString()}`;
      await navigator.clipboard.writeText(url);
      toast({
        type: 'success',
        message: 'Annotation URL copied to clipboard.',
        lifespan: 5000,
      });
    }
  };

  const handleDeleteAnnotation = async () => {
    if (annotation) {
      await deleteAnnotation.mutateAsync({
        annotationId: annotation.id,
      });
      clearMeasurements();
      cancelEditAnnotation();
      setEnabledDeletionModal(false);
    }
  };

  const destroy2DSketches = () => {
    for (const group of annotationGroups) {
      for (const anno of group.annotations) {
        if (anno.sketch2D && anno.photo === annotation?.photo) {
          api2D?.editor.hide(anno.sketch2D);
        }
      }
    }
  };

  // Handle toggling the edit annotation button
  const handleEditAnnotation = async () => {
    if (!annotation) return;
    useViewer.setState({ enabledDraw3D: null, enabledDraw2D: null });

    await handleViewAssociatedImage();

    let sketch3D;
    let sketch2D;
    // Here, we take the sketch details from the annotation draft if available; otherwise, we take them from the annotation as a fallback.
    // However, we take the sketch3D data from the annotation only during the first edit. From the second time onward, it's already available in the annotation draft.
    if (annotationDraft?.sketch3D) {
      annotationDraft.sketch3D.hide();
      sketch3D = annotationDraft?.sketch3D.getModel() as Sketch;
    } else if (annotation?.sketch3D) {
      annotation.sketch3D.hide();
      sketch3D = annotation.sketch3D.getModel() as Sketch;
    }

    if (annotationDraft?.sketch2D) {
      api2D?.editor.hide(annotationDraft?.sketch2D);
      sketch2D = annotationDraft.sketch2D;
    } else if (annotation.sketch2D) {
      api2D?.editor.hide(annotation?.sketch2D);
      sketch2D = annotation.sketch2D;
    }

    if (sketch3D) {
      const transform = new Matrix4().compose(
        sketch3D.getPosition(),
        sketch3D.getRotation(),
        sketch3D.getScale(),
      );
      const vertices = sketch3D.getVertices().map(vertex => vertex.applyMatrix4(transform));
      setTargetAnnotation3D(annotation);
      startDraw3D(
        vertices,
        shapeType === 'Polygon',
        new Color(getAnnotationColor(annotationDraft)),
        true,
        true,
        true,
      );

      // Cache current annotationDraft
      setAnnotationOriginalCache(annotationDraft);
    } else if (sketch2D) {
      setTargetAnnotation2D(annotation);
      startDraw2D(
        sketch2D?.getVertices(),
        sketch2D instanceof PolygonSketch2,
        new Color(annotationDraft?.color ?? '#FFFFFF'),
        true,
        true,
        true,
      );
    }
  };

  const handleSave = async () => {
    if (!annotationDraft) return;
    setLoading(true);
    if (annotationDraft?.mode === 'create') {
      const annotation: CreateAnnotationInput = {
        name: annotationDraft.name?.length ? annotationDraft.name : presetName,
        annotation2d: annotationDraft.annotation2d,
        annotation3d: getAnnotation3dMutationInput(annotationDraft),
        color: annotationDraft.color,
        projectId,
        templateId: annotationDraft.templateId,
        groupId: annotationDraft.groupId,
        fields: annotationDraft.fields,
      };
      if (targetProcess?.kind === 'annotation') {
        annotation.processId = targetProcess?.id;
      }
      await createAnnotation.mutateAsync({
        annotation,
      });

      // Cache the select fields
      const selectFields = annotationDraft.fields?.filter(field => field?.type === 'SELECT');
      setCurrentAnnotation(
        annotationDraft.color,
        annotationDraft.template,
        annotationDraft.groupId,
        selectFields as AnnotationSelectField[],
      );

      // Create a temporary annotation while the group is refetching
      const group = annotationGroups.find(group => group.id === annotation.groupId);
      if (api3D && api2D && group) {
        const temporary = await transformCreateAnnotation(
          {
            ...annotation,
            annotationId: 'TEMPORARY-ANNOTATION-ID',
            id: 'TEMPORARY-ANNOTATION-VERSION-ID',
          },
          group,
          annotationDraft.template as AnnotationTemplate,
          photo2DGroups,
          api3D,
          undefined,
        );
        if (temporary) {
          await temporary.sketch3D?.show();
          if (temporary.sketch2D) {
            api2D.editor.show(temporary.sketch2D);
          }
          group.annotations.push(temporary);
        }
      }
    } else if (annotationDraft?.mode === 'update' || annotationDraft?.mode === 'preview') {
      await updateAnnotation.mutateAsync({
        annotation: {
          annotation2d: annotationDraft.annotation2d,
          annotation3d: getAnnotation3dMutationInput(annotationDraft),
          annotationId: annotationDraft.annotationId,
          color: annotationDraft.color,
          groupId: annotationDraft.groupId,
          fields:
            annotationDraft.fields?.map(field => ({
              ...field,
              __typename: undefined,
              file: undefined,
            })) ?? [],
          name: annotationDraft.name,
        },
      });
    }
    clearMeasurements();
    setLoading(false);
  };

  const clearMeasurements = () => {
    api3D?.sectioning.hide();
    api3D?.volumetric.hide();
  };

  const handleCancelDeletionModal = () => {
    setEnabledDeletionModal(false);
  };

  const handleOpenDeletionModal = () => {
    setEnabledDeletionModal(true);
  };
  const handleSubmitCancellationModal = () => {
    setEnabledCancellationModal(false);
  };

  const handleCancelCancellationModal = () => {
    cancelEditAnnotation();
    clearMeasurements();
  };

  const handleCloseAnnotationPopupMenu = () => {
    if (
      ((volumetrics || sections) &&
        annotationDraft?.annotation3d &&
        annotationOriginal?.annotation3d &&
        !shareLinkToken &&
        getMeasurementDifferences(
          annotationDraft?.annotation3d,
          annotationOriginal?.annotation3d,
        )) ||
      (annotationOriginal == null && (volumetrics || sections))
    ) {
      setEnabledSaveCalculationModal(true);
    } else {
      clearMeasurements();
      cancelEditAnnotation();
      if (targetAnnotation3D) annotationDraft?.sketch3D?.hide();
    }
  };

  const handleCancelShapeTypeInfoModal = () => {
    if (!annotationOriginalCache) return;
    annotationDraft?.sketch3D?.hide();
    stopDraw3D(false);
    // Show the previous annotation sketch and apply its details to the annotation draft.
    annotationOriginalCache.sketch3D?.show();
    setAnnotationOriginal(annotationOriginalCache);
    setEnableShapeTypeInfoModal(false);
    setAnnotationOriginalSectionsCache(null);
    setAnnotationOriginalVolumetricsCache(null);
  };

  const handleSubmitTypeInfoModal = () => {
    stopDraw3D(false);
    annotationDraft?.sketch3D?.show();
    setEnableShapeTypeInfoModal(false);
    setAnnotationOriginalCache(null);
    setAnnotationOriginalSectionsCache(null);
    setAnnotationOriginalVolumetricsCache(null);
    toast({
      type: 'success',
      message: `The shape type of ${annotation?.name} has changed.`,
      lifespan: 10000,
      clickToDismiss: true,
    });
  };

  const handleCancelSaveCalculationModal = () => {
    setEnabledSaveCalculationModal(false);
  };

  const handleUpdateSaveCalculationModal = async () => {
    await handleSave();
    setEnabledSaveCalculationModal(false);
  };

  const handleDontSaveCalculationModal = () => {
    clearMeasurements();
    cancelEditAnnotation();
    if (targetAnnotation3D) annotationDraft?.sketch3D?.hide();
    setEnabledSaveCalculationModal(false);
  };

  // Get the selected layer's model node
  const getModelNode = useCallback(
    async (layer: Layer): Promise<Model3D | null> => {
      if (layer.sceneNode.constructor === SceneNode) {
        const error = await loadLayerModel(layer);
        if (error) {
          useExplore.setState({ placeLayerTarget: layer });
          showLocationAlertModal();
        }
      }
      if (layer.sceneNode instanceof ModelNode) {
        return layer.sceneNode.getModel() as Model3D;
      }
      return null;
    },
    [showLocationAlertModal],
  );

  // Compute progress callback
  const onComputeProgress: VolumetricProgressCallback | SectioningProgressCallback = (
    stage,
    progress,
  ) => {
    setLoadingProgress(progress);
    switch (stage) {
      case 'refine':
        setLoadingStage('Querying geometry...');
        break;
      case 'compute':
        setLoadingStage('Calculating...');
        break;
    }
  };

  // Compute volumetrics
  const computeVolumetrics = useCallback(
    async (layer: Layer, baseMode: VolumetricBaseMode, isCreate: boolean) => {
      if (!metadata?.points) return;
      setIsLoading(true);
      setLoadingProgress(0);
      setLoadingStage('Fetching...');
      const model = await getModelNode(layer);
      if (api3D && model) {
        api3D.volumetric.clear();
        const volumetrics = await api3D.volumetric.compute(
          metadata.points,
          baseMode,
          model,
          onComputeProgress,
        );
        setVolumetrics(volumetrics);
        if (isCreate) {
          setAnnotationMeasurements({
            baseSurface: baseMode.toString(),
            cutVolume: volumetrics.cut,
            fillVolume: volumetrics.fill,
            layerId: layer.id,
            maxElevation: volumetrics.maxElevation,
            minElevation: volumetrics.minElevation,
            projectedArea: volumetrics.projectedArea,
            surfaceArea: volumetrics.surfaceArea,
          });
        }
      }
      setLoadingProgress(0);
      setIsLoading(false);
    },
    [api3D, getModelNode, metadata?.points],
  );

  // Compute sectioning
  const computeSectioning = useCallback(
    async (layer: Layer, isCreate: boolean) => {
      if (!metadata?.points) return;
      setIsLoading(true);
      setLoadingProgress(0);
      setLoadingStage('Fetching...');
      const model = await getModelNode(layer);
      if (api3D && model) {
        api3D.sectioning.clear();
        const sections = await api3D.sectioning.compute(metadata.points, model, onComputeProgress);
        setSections(sections);
        if (isCreate) {
          setAnnotationMeasurements({
            hasSectioning: true,
            layerId: layer.id,
          });
        }
      }
      setLoadingProgress(0);
      setIsLoading(false);
    },
    [api3D, getModelNode, metadata?.points],
  );

  // Transform created annotation info to the update annotation
  useEffect(() => {
    if (createdAnnotationInfo) {
      const annotationGroup = annotationGroups.find(
        el => el.id === createdAnnotationInfo.annotationGroupId,
      );
      const annotation = annotationGroup?.annotations.find(
        el => el.id === createdAnnotationInfo.annotationId,
      );
      if (annotation) {
        const annotationData = {
          ...annotation.metadata,
          sketch3D: annotation.sketch3D,
        };
        startUpdateAnnotation(annotationData, annotation.template);
        setCreatedAnnotationInfo(null);
      }
    }
  }, [annotationGroups, createdAnnotationInfo]);

  // On close, show the annotations
  useEffect(() => {
    return () => {
      setTargetAnnotation2D(null);
      setTargetAnnotation3D(null);
      setAnnotationOriginalCache(null);
      setLastCreatedAnnotationId(null);
      setAnnotationOriginalSectionsCache(null);
      setAnnotationOriginalVolumetricsCache(null);
      useViewer.setState({ enabledDraw3D: null, enabledDraw2D: null });
    };
  }, []);

  // Enable the shape type info modal if the shape type has changed
  useEffect(() => {
    if (!annotationOriginalCache || !annotationDraft) return;
    if (
      annotationDraft.annotation3d?.shapeType !== annotationOriginalCache.annotation3d?.shapeType &&
      annotationOriginalCache.annotation3d?.layerId &&
      annotationDraft.id === annotationOriginalCache.id
    ) {
      setEnableShapeTypeInfoModal(true);
      // Store sections/volumetrics in annotationOriginalSectionsCache/annotationOriginalVolumetricsCache to populate measurement tab details temporarily since annotationDraft is being updated with the new sketch
      setAnnotationOriginalSectionsCache(sections);
      setAnnotationOriginalVolumetricsCache(volumetrics);
    }
    // Needs to avoid section/voluemetrics from dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotationDraft, annotationDraft?.annotation3d?.shapeType, annotationOriginalCache]);

  // If backend data is available, compute volumetrics/sectioning initially.
  useEffect(() => {
    if (sections || volumetrics) return;
    if (annotationDraft?.annotation3d?.layerId) {
      const layerId = annotationDraft?.annotation3d?.layerId;
      const layer = layers.find(layer => layer.id === layerId) ?? null;
      setSelectedLayer(layer);

      if (annotationDraft.annotation3d.baseSurface && layer) {
        let baseMode: VolumetricBaseMode;
        if (!isNaN(parseFloat(annotationDraft.annotation3d.baseSurface))) {
          baseMode = parseFloat(annotationDraft.annotation3d.baseSurface);
          setBaseHeight(baseMode);
          setBaseSurface('custom');
        } else {
          baseMode = annotationDraft.annotation3d.baseSurface as VolumetricBaseMode;
          setBaseSurface(baseMode as VolumetricBaseOption);
        }
        setVolumetrics({
          cut: annotationDraft.annotation3d?.cutVolume ?? 0,
          fill: annotationDraft.annotation3d?.fillVolume ?? 0,
          maxElevation: annotationDraft.annotation3d?.maxElevation ?? 0,
          minElevation: annotationDraft.annotation3d?.minElevation ?? 0,
          projectedArea: annotationDraft.annotation3d?.projectedArea ?? 0,
          surfaceArea: annotationDraft.annotation3d?.surfaceArea ?? 0,
        });

        computeVolumetrics(layer, baseMode, false);
      } else if (annotationDraft.annotation3d.hasSectioning && layer) {
        computeSectioning(layer, false);
      }
    }
  }, [
    annotationDraft,
    api3D,
    computeSectioning,
    computeVolumetrics,
    getModelNode,
    layers,
    metadata?.points,
    sections,
    volumetrics,
  ]);

  useEffect(() => {
    if (!annotationDraft?.annotation3d?.layerId) {
      setSections(null);
      setVolumetrics(null);
    }
  }, [annotationDraft?.annotation3d?.layerId]);

  // Clear sections/volumetrics when annotation is changed
  useEffect(() => {
    if (sections || volumetrics) {
      setTargetAnnotation3D(null);
      setSections(null);
      setVolumetrics(null);
      setAnnotationOriginalSectionsCache(null);
      setAnnotationOriginalVolumetricsCache(null);
      setLoadingStage('');
    }

    // Needs to avoid section/voluemetrics from dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotationDraft?.annotationId]);

  // Update sketch and mesh overlay visibility
  useEffect(() => {
    if (isEditingAnnotationSketch) {
      api3D?.volumetric.hide();
      api3D?.sectioning.hide();
      api3D?.draw.getFreeDrawController().show();
      api3D?.draw.getRectDrawController().show();
      annotationDraft?.sketch3D?.hide();
      const sketch3D = annotationDraft?.sketch3D?.getModel() as Sketch;
      sketch3D?.hideMeasurements();
      if (annotation) setTargetAnnotation3D(annotation);
    } else if (loadingStage === 'Calculating...') {
      const color = new Color(getAnnotationColor(annotationDraft));
      if (annotationDraft?.annotation3d?.baseSurface) {
        api3D?.volumetric.show();
        api3D?.volumetric.setColor(color);
        annotationDraft.sketch3D?.hide();
        api3D?.draw.getFreeDrawController().hide();
        api3D?.draw.getRectDrawController().hide();
        if (annotation) setTargetAnnotation3D(annotation);
      }
      if (annotationDraft?.annotation3d?.hasSectioning) {
        api3D?.sectioning.show();
        api3D?.sectioning.setColor(color);
        annotationDraft.sketch3D?.hide();
        api3D?.draw.getFreeDrawController().hide();
        api3D?.draw.getRectDrawController().hide();
        if (annotation) setTargetAnnotation3D(annotation);
      }
    } else if (annotationDraft?.annotation3d?.layerId) {
      annotationDraft?.sketch3D?.show();
    } else {
      api3D?.draw.getFreeDrawController().show();
      api3D?.draw.getRectDrawController().show();
    }
    // Reset everything
    return () => {
      api3D?.volumetric.hide();
      api3D?.sectioning.hide();
      api3D?.draw.getFreeDrawController().show();
      api3D?.draw.getRectDrawController().show();
      const sketch3D = annotationDraft?.sketch3D?.getModel() as Sketch;
      sketch3D?.hideMeasurements();
    };
  }, [
    annotation,
    annotationDraft,
    api3D,
    enabledDraw3D,
    isEditingAnnotationSketch,
    loadingStage,
    sections,
    volumetrics,
  ]);

  const { Tabs, TabsList, TabsTrigger, TabsContent } = useTabsComponents();

  const isSaveButtonDisabledBecauseRequiredFieldsAreNotFilled =
    (isPresetNameEnabled ? false : !annotationDraft?.name) ||
    !annotationDraft?.templateId ||
    !annotationDraft?.groupId;
  return (
    <PopupMenu
      containerStyles={cn(
        'w-[387px]',
        'flex',
        'bg-neutral-100',
        'rounded-2',
        'border-1px',
        'border-solid',
        'border-neutral-400',
      )}
      disableCloseButton
      y={40}
    >
      <div className="w-full p-3">
        <div className="w-full flex items-center justify-between">
          <p className={cn('typo-text-l', 'color-neutral-800', 'pointer-events-none')}>
            Annotation
          </p>

          {annotationDraft?.mode !== 'create' && (
            <div className="flex flex-row gap-[1px]">
              {!shareLinkToken && (
                <AnnotationPopupIconButton
                  icon={<PencilIcon height={16} width={16} />}
                  isActive={isEditingAnnotationSketch}
                  onClick={handleEditAnnotation}
                  tooltip="Edit annotation"
                />
              )}

              <AnnotationPopupIconButton
                analyticsEventObject={ANALYTICS_EVENT_OBJECT.FLY_TO_ANNOTATION}
                icon={<div className={cn('i-skand-flyto text-18px mt-1px')} />}
                isDisabled={enabledPanoramaWalkthrough}
                onClick={handleFlyTo}
                tooltip="Fly to annotation"
              />

              <AnnotationPopupIconButton
                analyticsEventObject={ANALYTICS_EVENT_OBJECT.SHARE_ANNOTATION}
                icon={<div className={cn('i-skand-sharelink text-16px')} />}
                onClick={handleShareLink}
                tooltip="Copy share link"
              />

              {!shareLinkToken && canEdit && (
                <AnnotationPopupIconButton
                  analyticsEventObject={ANALYTICS_EVENT_OBJECT.DELETE_ANNOTATION}
                  icon={<div className={cn('i-skand-trashbin text-16px')} />}
                  onClick={handleOpenDeletionModal}
                  tooltip="Delete annotation"
                />
              )}

              <AnnotationPopupIconButton
                icon={<div className={cn('i-skand-close text-14px')} />}
                onClick={handleCloseAnnotationPopupMenu}
                tooltip="Close"
              />
            </div>
          )}
        </div>
        {annotationDraft?.annotation3d?.shapeType ? (
          <Tabs
            className="skand-rnd-disable w-full"
            defaultValue="details"
            onValueChange={setCurrentTab}
            value={currentTab}
          >
            <TabsList className="my-3">
              <TabsTrigger className="flex items-center gap-6px" value="details">
                Details
                {isSaveButtonDisabledBecauseRequiredFieldsAreNotFilled && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="i-skand-warn text-3 color-warning-400" />
                    </TooltipTrigger>
                    <TooltipContent className="normal-case">
                      <span className={chip({ intent: 'warning' })}>
                        <span className={chipIcon({ intent: 'warning' })} />
                        Complete empty fields to create annotation.
                      </span>
                    </TooltipContent>
                  </Tooltip>
                )}
              </TabsTrigger>
              <TabsTrigger value="measurements">Measurements</TabsTrigger>
            </TabsList>

            <TabsContent value="details">
              <AnnotationDetails
                hasJustSwitchedToDetailsTab={hasJustSwitchedToDetailsTab}
                presetName={presetName}
                setPresetName={setPresetName}
              />
            </TabsContent>
            <TabsContent value="measurements">
              {metadata && metadata.points && (
                <AnnotationMeasurements
                  baseHeight={baseHeight}
                  baseSurface={baseSurface}
                  computeSectioning={computeSectioning}
                  computeVolumetrics={computeVolumetrics}
                  isLoading={isLoading}
                  loadingProgress={loadingProgress}
                  loadingStage={loadingStage}
                  points={metadata.points}
                  sections={sections}
                  selectedLayer={selectedLayer}
                  setBaseHeight={setBaseHeight}
                  setBaseSurface={setBaseSurface}
                  setSections={setSections}
                  setSelectedLayer={setSelectedLayer}
                  setVolumetrics={setVolumetrics}
                  shapeType={shapeType}
                  volumetrics={volumetrics}
                />
              )}
            </TabsContent>
          </Tabs>
        ) : (
          <div className="mt-3">
            <AnnotationDetails presetName={presetName} setPresetName={setPresetName} />
          </div>
        )}
        <div
          className={cn(
            'flex-row',
            'justify-stretch',
            'gap-2',
            'sticky',
            'bottom-0',
            'flex-0',
            'pt-3',
            'bg-neutral-100',
            currentTab === 'measurements' && shapeType === 'Point' ? 'hidden' : 'flex',
          )}
        >
          {annotationDraft?.mode === 'create' && (
            <Button
              className={cn('flex-1')}
              onClick={() => setEnabledCancellationModal(true)}
              primary
              size="s"
            >
              Close
            </Button>
          )}
          <Tooltip disableHoverableContent={!isSaveButtonDisabledBecauseRequiredFieldsAreNotFilled}>
            <TooltipTrigger asChild>
              <Button
                className={cn('flex-1')}
                disabled={
                  loading ||
                  isSaveButtonDisabledBecauseRequiredFieldsAreNotFilled ||
                  groupOptions.length === 0 ||
                  !isDirty ||
                  !!shareLinkToken ||
                  !canSave
                }
                filled
                onClick={handleSave}
                primary
                size="s"
              >
                {annotationDraft?.mode === 'create'
                  ? 'Create'
                  : annotationDraft?.mode === 'update'
                  ? 'Update'
                  : annotationDraft?.mode === 'preview'
                  ? 'Revert'
                  : 'Save'}
              </Button>
            </TooltipTrigger>
            {isSaveButtonDisabledBecauseRequiredFieldsAreNotFilled && (
              <TooltipContent>
                <span className={chip({ intent: 'warning' })}>
                  <span className={chipIcon({ intent: 'warning' })} />
                  Complete empty fields to create annotation.
                </span>
              </TooltipContent>
            )}
          </Tooltip>
        </div>
      </div>

      <DeletionModal
        annotationName={annotation?.name}
        onCancel={handleCancelDeletionModal}
        onSubmit={handleDeleteAnnotation}
        open={enabledDeletionModal}
      />

      <CancellationModal
        onCancel={handleCancelCancellationModal}
        onSubmit={handleSubmitCancellationModal}
        open={enabledCancellationModal}
      />

      <ShapeTypeInfoModal
        onCancel={handleCancelShapeTypeInfoModal}
        onSubmit={handleSubmitTypeInfoModal}
        open={enabledShapeTypeInfoModal}
      />

      <SaveCalculationModal
        onCancel={handleCancelSaveCalculationModal}
        onDontSave={handleDontSaveCalculationModal}
        onUpdate={handleUpdateSaveCalculationModal}
        open={enabledSaveCalculationModal}
      />
    </PopupMenu>
  );
};
